import Image from 'components/image'
import { truncate } from 'lodash'
import classNames from 'classnames'
import { InfoButton, LinkButton } from 'components/buttons'
import OfferList from 'components/offer-list/offer-list'
import useWindowSize from 'lib/hooks/useWindowSize'
import Link from 'components/link'
import { withContextualizedTracking, track } from 'lib/analytics'
import css from './styles.scss'
import { getSearchPreviewIds } from 'components/global-nav/components/SearchBar/SearchBar.utils'

export interface ProductCollectionProps {
  anchor: string
  sponsored?: boolean
  sponsoredHeading?: string
  sponsoredBrandLogo?: {
    description: string
    url: string
  }
  sponsoredLinkUrl?: string
  headline?: string
  description?: string
  products: Array<{
    id?: number
    brand?: string
    generic_product_name?: string
    image: { url: string }
    name: string
    short_name: string
    teaser: string
    normal_url: string
    offers: Array<{
      name: string
      normal_url: string
      price: string
      store: { name: string }
      url: string
      viglink_enabled: boolean
    }>
    url: string
  }>
  primaryCTAOption: string
}

export const SponsoredHeader = ({ heading, logo, url }: any) => (
  <Link newWindow className="text-body link-body block" url={url}>
    <div className="xsmall text-uppercase">{heading || 'Sponsored'}</div>
    {logo && <Image className="mts" src={logo} width={150} />}
  </Link>
)

const handleProductClick = (product: any) => {
  let price = product.offers[0]?.price
  if (price && typeof price === 'string') {
    price = price.replace('$', '')
    price = parseFloat(price)
  }
  const searchPreviewIds = getSearchPreviewIds()
  track.productClicked({
    productIds: [product.id],
    productPrice: price || 0,
    productType: track.ProductType.PRODUCT,
    eventLocation: track.EventLocation.GUIDE,
    searchPreviewIds,
  })
}

const handleAddToRegistry = (product: any) => {
  bl.addToRegistry({
    images: product.image.url,
    price: product.offers[0]?.price || '',
    product_id: product.id,
    title: product.name,
    url: product.normal_url,
    source: 'hello_baby',
  })
}

const ProductCollection = ({
  anchor,
  sponsored,
  sponsoredHeading,
  sponsoredBrandLogo,
  sponsoredLinkUrl,
  headline,
  description,
  products,
  primaryCTAOption,
}: ProductCollectionProps) => {
  const { width } = useWindowSize()

  const ActionButtons = ({ product }: any) => {
    const primaryOffer = product.offers[0]
    const pdpUrl = primaryOffer?.url
    const viglinkEnabled = primaryOffer?.viglink_enabled
    const linkRel = []

    if (!viglinkEnabled) {
      linkRel.push('norewrite')
    }

    if (pdpUrl && !pdpUrl.includes('babylist.com')) {
      linkRel.push('nofollow')
    }

    switch (primaryCTAOption) {
      case 'Add to Babylist':
        return (
          <InfoButton
            pill
            className="mvm phxl"
            size="small"
            onClick={() => handleAddToRegistry(product)}
          >
            Add to Babylist
          </InfoButton>
        )
      default:
        return (
          <>
            <InfoButton
              newWindow
              pill
              className="mvm phxl"
              href={pdpUrl}
              rel={linkRel.join(' ')}
              size="small"
            >
              Buy Now
            </InfoButton>
            <div>
              <LinkButton
                className="link-info man pan text-underline h7"
                onClick={() => handleAddToRegistry(product)}
              >
                Add to Babylist
              </LinkButton>
            </div>
          </>
        )
    }
  }

  return (
    <div>
      {headline && (
        <div className="text-center">
          <div className="product-section-anchor" id={anchor} />
          {sponsored && (
            <SponsoredHeader
              heading={sponsoredHeading}
              logo={sponsoredBrandLogo?.url}
              url={sponsoredLinkUrl}
            />
          )}
          <h3 className={classNames('product-headline', { mbm: !description })}>
            {headline}
          </h3>
          {description && (
            <div
              className="phm"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
      )}
      <div className={css.productGrid}>
        {products.map((product) => {
          const pdpUrl = product.url
          const viglinkEnabled = product.offers[0]?.viglink_enabled
          const linkRel = []

          if (!viglinkEnabled) {
            linkRel.push('norewrite')
          }

          if (pdpUrl && !pdpUrl.includes('babylist.com')) {
            linkRel.push('nofollow')
          }

          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div
              className={css.item}
              onClick={() => handleProductClick(product)}
            >
              <Link
                newWindow
                rel={`${pdpUrl?.includes('babylist.com') ? '' : 'nofollow'}`}
                url={pdpUrl}
              >
                <Image
                  alt={product.name}
                  className="img-responsive mbm"
                  lazyLoad={false}
                  src={product.image.url}
                />
              </Link>

              <div className="text-center">
                <div className={classNames(css.info, 'phm')}>
                  <h4>
                    {product.brand && (
                      <span className="tiny text-uppercase">
                        {product.brand}&nbsp;
                      </span>
                    )}
                    <br />
                    <span
                      className={classNames(
                        css.productName,
                        'text-bold mbs lh-md'
                      )}
                    >
                      <Link
                        newWindow
                        className="link-body-text"
                        rel={linkRel.join(' ')}
                        url={pdpUrl}
                      >
                        {truncate(product.generic_product_name, {
                          length: width && width < 660 ? 32 : 60,
                        })}
                      </Link>
                    </span>
                  </h4>
                  {product.teaser && (
                    <div className="h6 text-center mtn mbs serif">
                      {product.teaser}
                    </div>
                  )}
                </div>
                <div className="phl-md text-center">
                  <ActionButtons product={product} />
                </div>
                {/* @ts-ignore */}
                <OfferList responsive className="mtl" offers={product.offers} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.GUIDE,
})(ProductCollection)
